import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", purple:"#7c3aed",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {width:"100%",padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

export function ConsentsManager() {
  const [tab, setTab] = useState("consents"); // "consents" | "questionnaires"
  return (
    <div>
      <div style={{display:"flex",gap:4,background:C.light,padding:3,borderRadius:9,border:"1px solid "+C.border,width:"fit-content",marginBottom:20}}>
        {[
          ["consents", "📋 Consent Templates"],
          ["questionnaires", "❓ Questionnaires"],
        ].map(([v, l]) => (
          <button key={v} onClick={()=>setTab(v)} style={{
            padding:"8px 18px",fontSize:13,fontWeight:tab===v?800:600,
            color:tab===v?"#fff":C.muted,
            background:tab===v?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"none",
            borderRadius:7,border:"none",cursor:"pointer",fontFamily:F,
          }}>{l}</button>
        ))}
      </div>
      {tab === "consents" && <ConsentTemplatesTab/>}
      {tab === "questionnaires" && <QuestionnairesTab/>}
    </div>
  );
}

function ConsentTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [services, setServices] = useState([]);
  const [templateServices, setTemplateServices] = useState({});
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    load();
    const ch = supabase.channel("consents-mgr")
      .on("postgres_changes", { event: "*", schema: "public", table: "consent_templates" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "consent_template_services" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const load = async () => {
    const { data: tmpls } = await supabase.from("consent_templates").select("*").order("name");
    setTemplates(tmpls || []);
    const { data: svcs } = await supabase.from("services").select("*").eq("active", true).order("category").order("name");
    setServices(svcs || []);
    const { data: ts } = await supabase.from("consent_template_services").select("*");
    const map = {};
    (ts||[]).forEach(r => {
      if (!map[r.template_id]) map[r.template_id] = [];
      map[r.template_id].push(r.service_id);
    });
    setTemplateServices(map);
    setLoading(false);
  };

  const addNew = () => {
    setSelected({ name:"", content:"", service_scope:"all", active:true, is_ai_generated:false, _services:[] });
    setShowForm(true);
  };

  // ★ Always re-fetch the current service links from the DB when opening
  //   the editor — relying on the in-memory `templateServices` cache hit a
  //   race where staff opened edit before the cache loaded, captured an
  //   empty `_services: []`, then save() wiped the real DB rows.
  const edit = async (t) => {
    const { data: links, error } = await supabase
      .from("consent_template_services")
      .select("service_id")
      .eq("template_id", t.id);
    if (error) {
      alert("Could not load current service assignments: " + error.message + "\n\nNot opening the editor so we don't accidentally clear them.");
      return;
    }
    setSelected({ ...t, _services: (links || []).map(r => r.service_id) });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this consent template?\n\nLink-to-service rows will be cleared. Any signed copies stay in client history (they get unlinked from this template but are kept for compliance).")) return;
    try {
      // 1. Unlink the service-mapping rows. Check the error this time —
      //    a silent failure here could leave a half-deleted template.
      const { error: unlinkErr } = await supabase.from("consent_template_services").delete().eq("template_id", id);
      if (unlinkErr) {
        alert("Could not unlink services from template: " + unlinkErr.message + "\n\nAborting delete to avoid orphaned data.");
        return;
      }
      // 2. Preserve signed history but cut the FK so the delete below isn't
      //    blocked. Signed consents stay in the client's record by name.
      const { error: orphanErr } = await supabase.from("signed_consents").update({ template_id: null }).eq("template_id", id);
      if (orphanErr) {
        alert("Could not orphan signed copies: " + orphanErr.message + "\n\nAborting delete.");
        return;
      }
      // 3. Now safe to delete the template
      const { error } = await supabase.from("consent_templates").delete().eq("id", id);
      if (error) {
        alert("Delete failed: " + error.message + "\n\nIf you keep seeing this, the template may have records in another table — let me know which template name.");
        return;
      }
      await load();
    } catch (e) {
      alert("Delete failed: " + (e.message || e));
    }
  };

  const save = async () => {
    if (!selected.name || !selected.content) { alert("Name and content are required"); return; }
    const selectedSvcIds = (selected._services || []).map(x => x); // shallow copy

    const cleanData = {
      name: selected.name,
      content: selected.content,
      category: selected.category || "General",
      service_scope: selected.service_scope || "all",
      active: selected.active !== false,
      is_ai_generated: selected.is_ai_generated || false,
      is_optional: !!selected.is_optional,
    };

    let tmplId = selected.id;

    if (selected.id) {
      const { error } = await supabase.from("consent_templates").update(cleanData).eq("id", selected.id);
      if (error) { alert("Failed to update: " + error.message); console.error(error); return; }
    } else {
      const insertData = { ...cleanData, id: Date.now() };
      const { data: created, error } = await supabase.from("consent_templates").insert([insertData]).select().single();
      if (error) { alert("Failed to create: " + error.message); console.error(error); return; }
      tmplId = created?.id;
    }

    // ★ DIFF-BASED SYNC — never blindly DELETE-then-INSERT.
    //   The previous code wiped all service links before re-inserting; if
    //   the INSERT failed (RLS, network, FK race), every assignment was
    //   lost silently. New approach:
    //     1. Read the CURRENT links from the DB.
    //     2. If scope=specific → INSERT only the new ones, DELETE only the
    //        removed ones. Each step error-checked.
    //     3. If scope=all → DELETE all links (only after a confirmed read).
    //   If any step fails, we surface a visible alert and stop — the
    //   template's text is still updated above, but staff knows the
    //   service assignments need a retry.
    if (tmplId) {
      const { data: currentLinks, error: readErr } = await supabase
        .from("consent_template_services")
        .select("service_id")
        .eq("template_id", tmplId);
      if (readErr) {
        alert("⚠️ Template text saved, but could NOT read current service assignments to update them: " + readErr.message + "\n\nAssignments left untouched.");
        await load();
        setShowForm(false);
        setSelected(null);
        return;
      }
      const currentSet = new Set((currentLinks || []).map(r => r.service_id));
      const targetSet = new Set(cleanData.service_scope === "specific" ? selectedSvcIds : []);

      const toAdd = [...targetSet].filter(id => !currentSet.has(id));
      const toRemove = [...currentSet].filter(id => !targetSet.has(id));

      if (toAdd.length > 0) {
        const rows = toAdd.map(sid => ({ template_id: tmplId, service_id: sid }));
        const { error: addErr } = await supabase.from("consent_template_services").insert(rows);
        if (addErr) {
          alert("⚠️ Some service assignments could NOT be added: " + addErr.message + "\n\nExisting assignments are untouched. Re-open Edit and try again.");
          await load();
          setShowForm(false);
          setSelected(null);
          return;
        }
      }
      if (toRemove.length > 0) {
        const { error: rmErr } = await supabase
          .from("consent_template_services")
          .delete()
          .eq("template_id", tmplId)
          .in("service_id", toRemove);
        if (rmErr) {
          alert("⚠️ Could not remove old service assignments: " + rmErr.message + "\n\nThe new ones were added; the old ones are still there. Re-open Edit and try again.");
          await load();
          setShowForm(false);
          setSelected(null);
          return;
        }
      }
    }

    await load();
    setShowForm(false);
    setSelected(null);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S}}>Consent Templates</div>
          <div style={{fontSize:12,color:C.muted,marginTop:2}}>Manage consent forms for treatments</div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={()=>setShowAIModal(true)} style={{padding:"10px 16px",background:"linear-gradient(135deg,#7c3aed,#5b21b6)",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>🤖 Generate with AI</button>
          <button onClick={addNew} style={{padding:"10px 18px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ Create Manually</button>
        </div>
      </div>

      {loading && <div style={{padding:40,textAlign:"center",color:C.muted}}>Loading...</div>}

      {!loading && templates.length === 0 && (
        <div style={{padding:60,textAlign:"center",background:C.light,borderRadius:12,border:"1px dashed "+C.border}}>
          <div style={{fontSize:40,marginBottom:10,opacity:0.3}}>📋</div>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:6}}>No consent templates yet</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Create one with AI or manually</div>
          <button onClick={()=>setShowAIModal(true)} style={{padding:"9px 16px",background:"linear-gradient(135deg,#7c3aed,#5b21b6)",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>🤖 Generate with AI</button>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:14}}>
        {templates.map(t => {
          const linkedSvcs = templateServices[t.id] || [];
          const linkedCount = linkedSvcs.length;
          return (
            <div key={t.id} style={{background:C.card,border:"1px solid "+C.border,borderLeft:"4px solid "+(t.is_ai_generated?"#7c3aed":C.gold),borderRadius:12,padding:16,opacity:t.active!==false?1:0.5}}>
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:10}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:15,fontWeight:800,color:C.text,fontFamily:S,marginBottom:4}}>{t.name}</div>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                    {t.is_ai_generated && <span style={{fontSize:9,padding:"2px 7px",background:"rgba(124,58,237,0.12)",color:"#7c3aed",borderRadius:8,fontWeight:700}}>🤖 AI</span>}
                    {t.is_optional && <span style={{fontSize:9,padding:"2px 7px",background:C.goldBg,color:C.goldD,borderRadius:8,fontWeight:700}}>⭐ OPTIONAL</span>}
                    <span style={{fontSize:9,padding:"2px 7px",background:t.service_scope==="all"?C.greenL:"rgba(37,99,235,0.1)",color:t.service_scope==="all"?C.green:C.blue,borderRadius:8,fontWeight:700}}>
                      {t.service_scope === "all" ? "ALL SERVICES" : `${linkedCount} SERVICE${linkedCount!==1?"S":""}`}
                    </span>
                  </div>
                </div>
                {t.active === false && <span style={{fontSize:9,padding:"2px 7px",background:C.muted,color:"#fff",borderRadius:8,fontWeight:700}}>INACTIVE</span>}
              </div>
              <div style={{fontSize:11,color:C.muted,lineHeight:1.5,maxHeight:60,overflow:"hidden",marginBottom:12,padding:"6px 0",borderTop:"1px solid "+C.border}}>
                {(t.content || "").replace(/<[^>]+>/g, "").substring(0, 120)}...
              </div>
              <div style={{display:"flex",gap:6}}>
                <button onClick={()=>edit(t)} style={{flex:1,padding:"6px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>Edit</button>
                <button onClick={()=>remove(t.id)} style={{padding:"6px 10px",background:C.redL,color:C.red,border:"1px solid "+C.red+"30",borderRadius:6,fontSize:11,cursor:"pointer",fontFamily:F}}>🗑</button>
              </div>
            </div>
          );
        })}
      </div>

      {showAIModal && <AIGeneratorModal onClose={()=>setShowAIModal(false)} onGenerated={(data)=>{setShowAIModal(false);setSelected({name:data.service_name+" Consent",content:data.content,service_scope:"specific",active:true,is_ai_generated:true,_services:data.service_ids||[]});setShowForm(true);}} services={services} generating={generating} setGenerating={setGenerating}/>}
      
      {showForm && selected && <ConsentFormModal consent={selected} setConsent={setSelected} services={services} onClose={()=>{setShowForm(false);setSelected(null);}} onSave={save}/>}
    </div>
  );
}

function AIGeneratorModal({ onClose, onGenerated, services, generating, setGenerating }) {
  const [selectedSvc, setSelectedSvc] = useState("");
  const [customDetails, setCustomDetails] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const byCategory = {};
  services.forEach(s => {
    if (!byCategory[s.category]) byCategory[s.category] = [];
    byCategory[s.category].push(s);
  });

  const generate = async () => {
    if (!selectedSvc) { alert("Please select a service"); return; }
    const svc = services.find(s => s.id.toString() === selectedSvc.toString());
    if (!svc) return;
    
    setGenerating(true);
    setErrorMsg("");

    try {
      const { data, error } = await supabase.functions.invoke("generate-consent", {
        body: {
          service_name: svc.name,
          service_category: svc.category,
          treatment_details: customDetails,
        }
      });

      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error || "Generation failed");

      onGenerated({ 
        service_name: svc.name, 
        content: data.content,
        service_ids: [svc.id]
      });
    } catch (e) {
      setErrorMsg("Error: " + e.message);
      setGenerating(false);
    }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:540,padding:28,maxHeight:"90vh",overflowY:"auto",position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:12,right:12,background:C.light,border:"none",borderRadius:7,padding:"4px 10px",cursor:"pointer",fontSize:16,color:C.muted}}>×</button>
        
        <div style={{textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:36,marginBottom:8}}>🤖</div>
          <div style={{fontSize:20,fontWeight:800,color:"#7c3aed",fontFamily:S,marginBottom:4}}>AI Consent Generator</div>
          <div style={{fontSize:12,color:C.muted}}>Powered by Claude - generates professional consent forms</div>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div>
            <label style={{fontSize:11,fontWeight:800,color:"#7c3aed",textTransform:"uppercase",display:"block",marginBottom:6}}>Select Service *</label>
            <select value={selectedSvc} onChange={e=>setSelectedSvc(e.target.value)} style={inp}>
              <option value="">Choose a service...</option>
              {Object.entries(byCategory).map(([cat, svcs]) => (
                <optgroup key={cat} label={cat}>
                  {svcs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label style={{fontSize:11,fontWeight:800,color:"#7c3aed",textTransform:"uppercase",display:"block",marginBottom:6}}>Additional Details (optional)</label>
            <textarea value={customDetails} onChange={e=>setCustomDetails(e.target.value)} rows={3} placeholder="e.g. 'Mention that we use Allergan brand' or 'Include specific aftercare: avoid sun for 48 hours'" style={{...inp,resize:"vertical"}}/>
            <div style={{fontSize:10,color:C.muted,marginTop:4,fontStyle:"italic"}}>Add specific instructions or facts you want included</div>
          </div>

          <div style={{padding:12,background:"rgba(124,58,237,0.08)",border:"1px solid rgba(124,58,237,0.2)",borderRadius:8,fontSize:11,color:C.text,lineHeight:1.5}}>
            <strong style={{color:"#7c3aed"}}>ℹ️ Claude will generate:</strong>
            <ul style={{margin:"6px 0 0 20px",padding:0}}>
              <li>Treatment description & benefits</li>
              <li>Risks, side effects, contraindications</li>
              <li>Pre & post-treatment instructions</li>
              <li>Photography & financial consents</li>
              <li>Signature sections</li>
            </ul>
            <div style={{marginTop:8,fontSize:10,fontStyle:"italic",color:C.muted}}>You can edit the generated consent before saving.</div>
          </div>

          {errorMsg && <div style={{padding:"10px 14px",background:C.redL,color:C.red,borderRadius:8,fontSize:12,fontWeight:600}}>{errorMsg}</div>}
        </div>

        <div style={{display:"flex",gap:8,marginTop:20,justifyContent:"flex-end"}}>
          <button onClick={onClose} disabled={generating} style={{padding:"10px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
          <button onClick={generate} disabled={generating || !selectedSvc} style={{padding:"10px 20px",background:(generating||!selectedSvc)?C.light:"linear-gradient(135deg,#7c3aed,#5b21b6)",color:(generating||!selectedSvc)?C.muted:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:(generating||!selectedSvc)?"not-allowed":"pointer",fontFamily:F}}>
            {generating?"🤖 Generating...":"🤖 Generate Consent"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConsentFormModal({ consent, setConsent, services, onClose, onSave }) {
  const servicesByCategory = {};
  (services||[]).forEach(s => {
    if (!servicesByCategory[s.category]) servicesByCategory[s.category] = [];
    servicesByCategory[s.category].push(s);
  });
  const selectedSvcs = consent._services || [];

  const toggleService = (id) => {
    const newList = selectedSvcs.includes(id) ? selectedSvcs.filter(x=>x!==id) : [...selectedSvcs, id];
    setConsent({...consent, _services: newList});
  };

  const toggleCategory = (cat) => {
    const catIds = servicesByCategory[cat].map(s=>s.id);
    const allIn = catIds.every(id => selectedSvcs.includes(id));
    const newList = allIn 
      ? selectedSvcs.filter(id => !catIds.includes(id))
      : [...new Set([...selectedSvcs, ...catIds])];
    setConsent({...consent, _services: newList});
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:700,padding:24,maxHeight:"92vh",overflowY:"auto",position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:12,right:12,background:C.light,border:"none",borderRadius:7,padding:"4px 10px",cursor:"pointer",fontSize:16,color:C.muted}}>×</button>
        
        <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:4}}>{consent.id?"Edit Consent Template":"Create Consent Template"}</div>
        {consent.is_ai_generated && <div style={{fontSize:10,color:"#7c3aed",marginBottom:14,padding:"3px 8px",background:"rgba(124,58,237,0.12)",borderRadius:6,display:"inline-block",fontWeight:700}}>🤖 AI GENERATED</div>}
        
        <div style={{display:"flex",flexDirection:"column",gap:12,marginTop:8}}>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Name *</label>
            <input value={consent.name} onChange={e=>setConsent({...consent,name:e.target.value})} placeholder="e.g. Botox Consent Form" style={inp}/>
          </div>

          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Content (HTML) *</label>
            <textarea value={consent.content||""} onChange={e=>setConsent({...consent,content:e.target.value})} rows={10} placeholder="Consent form content..." style={{...inp,resize:"vertical",fontFamily:"monospace",fontSize:11}}/>
            <div style={{fontSize:10,color:C.muted,marginTop:4,fontStyle:"italic"}}>HTML tags allowed: &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;</div>
          </div>

          <div style={{padding:14,background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:10}}>
            <div style={{fontSize:11,fontWeight:800,color:C.gold,textTransform:"uppercase",marginBottom:10}}>🎯 Which Services Need This Consent?</div>
            
            <div style={{display:"flex",gap:10,marginBottom:10}}>
              <label style={{flex:1,display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"10px 12px",background:consent.service_scope==="all"?C.greenL:"#fff",border:"1.5px solid "+(consent.service_scope==="all"?C.green:C.border),borderRadius:8}}>
                <input type="radio" checked={consent.service_scope==="all"} onChange={()=>setConsent({...consent,service_scope:"all"})} style={{accentColor:C.green}}/>
                <span style={{fontSize:12,fontWeight:700,color:C.text}}>All services</span>
              </label>
              <label style={{flex:1,display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"10px 12px",background:consent.service_scope==="specific"?"rgba(37,99,235,0.08)":"#fff",border:"1.5px solid "+(consent.service_scope==="specific"?C.blue:C.border),borderRadius:8}}>
                <input type="radio" checked={consent.service_scope==="specific"} onChange={()=>setConsent({...consent,service_scope:"specific"})} style={{accentColor:C.blue}}/>
                <span style={{fontSize:12,fontWeight:700,color:C.text}}>Specific services</span>
              </label>
            </div>

            {consent.service_scope === "specific" && (
              <div style={{maxHeight:280,overflowY:"auto",background:"#fff",borderRadius:8,padding:10}}>
                {Object.entries(servicesByCategory).map(([cat, svcs]) => {
                  const catIds = svcs.map(s=>s.id);
                  const allIn = catIds.every(id => selectedSvcs.includes(id));
                  const someIn = catIds.some(id => selectedSvcs.includes(id));
                  return (
                    <div key={cat} style={{marginBottom:10}}>
                      <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:6,paddingBottom:6,borderBottom:"1px solid "+C.border}}>
                        <input type="checkbox" checked={allIn} ref={el=>{if(el)el.indeterminate=someIn&&!allIn;}} onChange={()=>toggleCategory(cat)} style={{accentColor:C.gold,width:14,height:14}}/>
                        <span style={{fontSize:11,fontWeight:800,color:C.gold,textTransform:"uppercase"}}>{cat}</span>
                        <span style={{fontSize:10,color:C.muted,marginLeft:"auto"}}>{svcs.filter(s=>selectedSvcs.includes(s.id)).length}/{svcs.length}</span>
                      </label>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:3}}>
                        {svcs.map(s => (
                          <label key={s.id} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",padding:"3px 6px",borderRadius:4,background:selectedSvcs.includes(s.id)?C.goldBg:"transparent"}}>
                            <input type="checkbox" checked={selectedSvcs.includes(s.id)} onChange={()=>toggleService(s.id)} style={{accentColor:C.gold}}/>
                            <span style={{fontSize:11,color:C.text}}>{s.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"10px 14px",background:consent.active!==false?C.greenL:C.light,borderRadius:8}}>
            <input type="checkbox" checked={consent.active!==false} onChange={e=>setConsent({...consent,active:e.target.checked})} style={{accentColor:C.gold}}/>
            <span style={{fontSize:12,color:C.text,fontWeight:600}}>Active (will be used at check-in)</span>
          </label>

          <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",padding:"12px 14px",background:consent.is_optional?C.goldBg:C.light,border:"1.5px solid "+(consent.is_optional?C.gold:C.border),borderRadius:8}}>
            <input type="checkbox" checked={!!consent.is_optional} onChange={e=>setConsent({...consent,is_optional:e.target.checked})} style={{accentColor:C.gold,marginTop:2,width:16,height:16}}/>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:C.text,display:"flex",alignItems:"center",gap:8}}>
                ⭐ Optional consent — ask before signing
              </div>
              <div style={{fontSize:11,color:C.muted,marginTop:3,lineHeight:1.4}}>
                Staff sees a pre-step where they can opt the patient in or skip this consent.
                Use for things like Social Media Release, marketing photos, etc.
              </div>
            </div>
          </label>
        </div>

        <div style={{display:"flex",gap:8,marginTop:20,justifyContent:"flex-end"}}>
          <button onClick={onClose} style={{padding:"9px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
          <button onClick={onSave} style={{padding:"9px 18px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>{consent.id?"Save Changes":"Create Template"}</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// QUESTIONNAIRES — yes/no intake forms tied to services
// ════════════════════════════════════════════════════════════
function QuestionnairesTab() {
  const [templates, setTemplates] = useState([]);
  const [services, setServices] = useState([]);
  const [tplServices, setTplServices] = useState({});
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    const ch = supabase.channel("q-templates-mgr")
      .on("postgres_changes", { event: "*", schema: "public", table: "questionnaire_templates" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "questionnaire_template_services" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const load = async () => {
    const [t, s, ts] = await Promise.all([
      supabase.from("questionnaire_templates").select("*").order("name"),
      supabase.from("services").select("*").eq("active", true).order("category").order("name"),
      supabase.from("questionnaire_template_services").select("*"),
    ]);
    setTemplates(t.data || []);
    setServices(s.data || []);
    const map = {};
    (ts.data || []).forEach(r => {
      if (!map[r.template_id]) map[r.template_id] = [];
      map[r.template_id].push(r.service_id);
    });
    setTplServices(map);
    setLoading(false);
  };

  const addNew = () => {
    setSelected({
      name: "",
      description: "",
      questions: [{ id: "q1", text: "", required: true }],
      service_scope: "all",
      active: true,
      _services: [],
    });
    setShowForm(true);
  };

  const edit = (t) => {
    setSelected({ ...t, _services: tplServices[t.id] || [] });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this questionnaire? Existing answers are not affected.")) return;
    await supabase.from("questionnaire_templates").delete().eq("id", id);
  };

  const save = async () => {
    if (!selected.name) { alert("Name is required"); return; }
    const questions = (selected.questions || []).filter(q => q.text && q.text.trim());
    if (questions.length === 0) { alert("Add at least one question"); return; }
    const selectedSvcIds = selected._services || [];
    const cleanData = {
      name: selected.name,
      description: selected.description || null,
      questions,
      service_scope: selected.service_scope || "all",
      active: selected.active !== false,
    };

    let tmplId = selected.id;
    if (selected.id) {
      const { error } = await supabase.from("questionnaire_templates").update(cleanData).eq("id", selected.id);
      if (error) { alert("Save failed: " + error.message); return; }
    } else {
      const { data: created, error } = await supabase.from("questionnaire_templates").insert([{ ...cleanData, id: Date.now() }]).select().single();
      if (error) { alert("Create failed: " + error.message); return; }
      tmplId = created?.id;
    }

    if (tmplId) {
      await supabase.from("questionnaire_template_services").delete().eq("template_id", tmplId);
      if (cleanData.service_scope === "specific" && selectedSvcIds.length > 0) {
        const rows = selectedSvcIds.map(sid => ({ template_id: tmplId, service_id: sid }));
        await supabase.from("questionnaire_template_services").insert(rows);
      }
    }

    await load();
    setShowForm(false);
    setSelected(null);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S}}>Intake Questionnaires</div>
          <div style={{fontSize:12,color:C.muted,marginTop:2}}>Yes/no questions clients answer during check-in — assign to specific services or all</div>
        </div>
        <button onClick={addNew} style={{padding:"10px 18px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ New Questionnaire</button>
      </div>

      {loading && <div style={{padding:40,textAlign:"center",color:C.muted}}>Loading…</div>}

      {!loading && templates.length === 0 && (
        <div style={{padding:60,textAlign:"center",background:C.light,borderRadius:12,border:"1px dashed "+C.border}}>
          <div style={{fontSize:40,marginBottom:10,opacity:0.3}}>❓</div>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:6}}>No questionnaires yet</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Build your first intake questionnaire. Each question is a yes/no that the client answers during check-in.</div>
          <button onClick={addNew} style={{padding:"9px 16px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ Create First Questionnaire</button>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:14}}>
        {templates.map(t => {
          const linkedCount = (tplServices[t.id] || []).length;
          const qCount = (t.questions || []).length;
          return (
            <div key={t.id} style={{background:C.card,border:"1px solid "+C.border,borderLeft:"4px solid "+C.gold,borderRadius:12,padding:16,opacity:t.active!==false?1:0.5}}>
              <div style={{fontSize:15,fontWeight:800,color:C.text,fontFamily:S,marginBottom:5}}>{t.name}</div>
              {t.description && <div style={{fontSize:11,color:C.muted,marginBottom:8,lineHeight:1.4}}>{t.description}</div>}
              <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:10}}>
                <span style={{fontSize:9,padding:"2px 7px",background:C.goldBg,color:C.gold,borderRadius:8,fontWeight:700}}>{qCount} QUESTION{qCount!==1?"S":""}</span>
                <span style={{fontSize:9,padding:"2px 7px",background:t.service_scope==="all"?C.greenL:"rgba(37,99,235,0.1)",color:t.service_scope==="all"?C.green:C.blue,borderRadius:8,fontWeight:700}}>
                  {t.service_scope === "all" ? "ALL SERVICES" : `${linkedCount} SERVICE${linkedCount!==1?"S":""}`}
                </span>
                {t.active === false && <span style={{fontSize:9,padding:"2px 7px",background:C.muted,color:"#fff",borderRadius:8,fontWeight:700}}>INACTIVE</span>}
              </div>
              <div style={{padding:"8px 10px",background:C.light,borderRadius:7,fontSize:11,color:C.muted,marginBottom:10,maxHeight:80,overflow:"hidden",lineHeight:1.5}}>
                {(t.questions || []).slice(0,3).map((q,i) => <div key={i}>• {q.text}</div>)}
                {qCount > 3 && <div style={{opacity:0.6,fontStyle:"italic"}}>+ {qCount-3} more…</div>}
              </div>
              <div style={{display:"flex",gap:6}}>
                <button onClick={()=>edit(t)} style={{flex:1,padding:"6px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>Edit</button>
                <button onClick={()=>remove(t.id)} style={{padding:"6px 10px",background:C.redL,color:C.red,border:"1px solid "+C.red+"30",borderRadius:6,fontSize:11,cursor:"pointer",fontFamily:F}}>🗑</button>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && selected && <QuestionnaireFormModal q={selected} setQ={setSelected} services={services} onClose={()=>{setShowForm(false);setSelected(null);}} onSave={save}/>}
    </div>
  );
}

function QuestionnaireFormModal({ q, setQ, services, onClose, onSave }) {
  const servicesByCat = {};
  (services || []).forEach(s => {
    if (!servicesByCat[s.category]) servicesByCat[s.category] = [];
    servicesByCat[s.category].push(s);
  });

  const updateQuestion = (idx, patch) => {
    const next = (q.questions || []).map((qq, i) => i === idx ? { ...qq, ...patch } : qq);
    setQ({ ...q, questions: next });
  };
  const addQuestion = () => {
    const next = [...(q.questions || []), { id: "q" + (Date.now() % 100000), text: "", required: true }];
    setQ({ ...q, questions: next });
  };
  const removeQuestion = (idx) => {
    const next = (q.questions || []).filter((_, i) => i !== idx);
    setQ({ ...q, questions: next });
  };
  const moveQuestion = (idx, dir) => {
    const arr = [...(q.questions || [])];
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= arr.length) return;
    [arr[idx], arr[tgt]] = [arr[tgt], arr[idx]];
    setQ({ ...q, questions: arr });
  };

  const selectedSvcs = q._services || [];
  const toggleService = (id) => {
    const next = selectedSvcs.includes(id) ? selectedSvcs.filter(x => x !== id) : [...selectedSvcs, id];
    setQ({ ...q, _services: next });
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:640,padding:24,maxHeight:"92vh",overflowY:"auto",position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:12,right:12,background:C.light,border:"none",borderRadius:7,padding:"4px 10px",cursor:"pointer",fontSize:16,color:C.muted}}>×</button>
        <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:18}}>{q.id ? "Edit Questionnaire" : "New Questionnaire"}</div>

        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5,letterSpacing:"0.08em"}}>Questionnaire Name *</label>
            <input value={q.name || ""} onChange={e=>setQ({...q,name:e.target.value})} placeholder="e.g. Botox Pre-Treatment Screening" style={inp}/>
          </div>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5,letterSpacing:"0.08em"}}>Description (optional)</label>
            <input value={q.description || ""} onChange={e=>setQ({...q,description:e.target.value})} placeholder="Short note about when this is used" style={inp}/>
          </div>

          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em"}}>Questions (Yes / No)</label>
              <button type="button" onClick={addQuestion} style={{padding:"5px 12px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ Add Question</button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {(q.questions || []).map((qq, i) => (
                <div key={i} style={{padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                    <div style={{fontSize:11,color:C.muted,fontWeight:700,minWidth:18}}>{i+1}.</div>
                    <input value={qq.text} onChange={e=>updateQuestion(i, { text: e.target.value })} placeholder={"e.g. Are you pregnant or breastfeeding?"} style={{...inp,background:"#fff"}}/>
                    <label style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:C.muted,cursor:"pointer",whiteSpace:"nowrap"}}>
                      <input type="checkbox" checked={qq.required !== false} onChange={e=>updateQuestion(i, { required: e.target.checked })} style={{accentColor:C.gold}}/>
                      Required
                    </label>
                    <button type="button" onClick={()=>moveQuestion(i, -1)} disabled={i===0} style={{padding:"4px 7px",background:"#fff",color:C.muted,border:"1px solid "+C.border,borderRadius:5,fontSize:11,cursor:i===0?"not-allowed":"pointer",opacity:i===0?0.4:1}}>↑</button>
                    <button type="button" onClick={()=>moveQuestion(i, 1)} disabled={i===(q.questions||[]).length-1} style={{padding:"4px 7px",background:"#fff",color:C.muted,border:"1px solid "+C.border,borderRadius:5,fontSize:11,cursor:i===(q.questions||[]).length-1?"not-allowed":"pointer",opacity:i===(q.questions||[]).length-1?0.4:1}}>↓</button>
                    <button type="button" onClick={()=>removeQuestion(i)} style={{padding:"4px 8px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:5,fontSize:11,cursor:"pointer"}}>🗑</button>
                  </div>
                  {/* Follow-up text input on "Yes" — for things like "Are you taking medications? If yes, please list" */}
                  <div style={{marginLeft:26,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <label style={{display:"flex",alignItems:"center",gap:5,fontSize:10,color:qq.follow_up_on_yes?C.gold:C.muted,cursor:"pointer",fontWeight:600}}>
                      <input type="checkbox" checked={!!qq.follow_up_on_yes} onChange={e=>updateQuestion(i, { follow_up_on_yes: e.target.checked })} style={{accentColor:C.gold}}/>
                      📝 If "Yes" → show text box to list details
                    </label>
                    {qq.follow_up_on_yes && (
                      <input
                        value={qq.follow_up_label || ""}
                        onChange={e=>updateQuestion(i, { follow_up_label: e.target.value })}
                        placeholder='Label, e.g. "Please list medications"'
                        style={{...inp,background:"#fff",fontSize:11,padding:"6px 9px",flex:1,minWidth:180}}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5,letterSpacing:"0.08em"}}>Apply to which services?</label>
            <div style={{display:"flex",gap:8,marginBottom:10}}>
              <label style={{flex:1,padding:"10px 12px",background:q.service_scope==="all"?C.goldBg:C.light,border:"1px solid "+(q.service_scope==="all"?C.gold:C.border),borderRadius:8,cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:600,color:q.service_scope==="all"?C.gold:C.text}}>
                <input type="radio" checked={q.service_scope==="all"} onChange={()=>setQ({...q,service_scope:"all"})} style={{accentColor:C.gold}}/>
                ALL services
              </label>
              <label style={{flex:1,padding:"10px 12px",background:q.service_scope==="specific"?C.goldBg:C.light,border:"1px solid "+(q.service_scope==="specific"?C.gold:C.border),borderRadius:8,cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:600,color:q.service_scope==="specific"?C.gold:C.text}}>
                <input type="radio" checked={q.service_scope==="specific"} onChange={()=>setQ({...q,service_scope:"specific"})} style={{accentColor:C.gold}}/>
                Specific services
              </label>
            </div>
            {q.service_scope === "specific" && (
              <div style={{padding:12,background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:10,maxHeight:220,overflowY:"auto"}}>
                {Object.entries(servicesByCat).map(([cat, svcs]) => (
                  <div key={cat} style={{marginBottom:10}}>
                    <div style={{fontSize:10,fontWeight:800,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{cat}</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                      {svcs.map(s => (
                        <label key={s.id} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",padding:"4px 6px",borderRadius:5,background:selectedSvcs.includes(s.id)?"#fff":"transparent"}}>
                          <input type="checkbox" checked={selectedSvcs.includes(s.id)} onChange={()=>toggleService(s.id)} style={{accentColor:C.gold}}/>
                          <span style={{fontSize:11,color:C.text}}>{s.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",background:C.light,borderRadius:8,cursor:"pointer"}}>
            <input type="checkbox" checked={q.active !== false} onChange={e=>setQ({...q,active:e.target.checked})} style={{accentColor:C.gold}}/>
            <span style={{fontSize:12,color:C.text,fontWeight:600}}>Active (will appear during check-in)</span>
          </label>
        </div>

        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18}}>
          <button onClick={onClose} style={{padding:"9px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
          <button onClick={onSave} style={{padding:"9px 20px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>{q.id ? "Save Changes" : "Create Questionnaire"}</button>
        </div>
      </div>
    </div>
  );
}

// Standalone runner used by CheckInFlow to administer a questionnaire during
// check-in. Renders applicable templates as yes/no question lists, validates
// required answers, then calls onComplete([{ template_id, template_name, answers:[…] }]).
export function QuestionnaireRunner({ appointment, onComplete, onCancel }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({}); // `${tplId}::${qid}` → 'yes'|'no'
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      // ★ MULTI-TREATMENT matching (fixed 2026-06-11) — previously only the
      //   FIRST service of "A + B" was matched, so a questionnaire assigned
      //   to B never appeared (Japneet: "PDGF Hair Injections + Morpheus
      //   Face" missed the Morpheus GFE entirely). Now every part of the
      //   treat string is matched, with normalization + word-overlap
      //   fallback — same strategy as the consent matcher.
      const [{ data: allTemplates }, { data: links }, { data: allServices }] = await Promise.all([
        supabase.from("questionnaire_templates").select("*").eq("active", true),
        supabase.from("questionnaire_template_services").select("*"),
        supabase.from("services").select("id,name"),
      ]);

      const normalize = (s) => (s || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
      const treatParts = (appointment?.treat || "").split(/\s*\+\s*/).map(normalize).filter(Boolean);

      // Pass 1 — exact normalized name match for every treatment part
      const matchedServiceIds = new Set(
        (allServices || []).filter(s => treatParts.includes(normalize(s.name))).map(s => s.id)
      );
      // Pass 2 — word-overlap fallback for parts that found nothing
      const matchedNorms = new Set((allServices || []).filter(s => matchedServiceIds.has(s.id)).map(s => normalize(s.name)));
      const STOP = new Set(["with", "and", "the", "for", "add", "on", "treatment", "injections", "injection"]);
      const words = (s) => new Set(normalize(s).split(" ").filter(w => w.length >= 4 && !STOP.has(w)));
      for (const part of treatParts) {
        if (matchedNorms.has(part)) continue;
        const pw = words(part);
        if (!pw.size) continue;
        for (const s of (allServices || [])) {
          if (matchedServiceIds.has(s.id)) continue;
          const sw = words(s.name);
          let hit = false;
          for (const w of pw) if (sw.has(w)) { hit = true; break; }
          if (hit) matchedServiceIds.add(s.id);
        }
      }

      const linksMap = {};
      (links || []).forEach(r => { if (!linksMap[r.template_id]) linksMap[r.template_id]=[]; linksMap[r.template_id].push(r.service_id); });
      const applicable = (allTemplates || []).filter(t =>
        t.service_scope === "all" ||
        (linksMap[t.id] || []).some(id => matchedServiceIds.has(id))
      );
      if (applicable.length === 0) {
        console.warn("[questionnaire] no templates matched for treat:", appointment?.treat, "— matched service ids:", [...matchedServiceIds]);
      }
      setTemplates(applicable);
      setLoading(false);
    })();
  }, [appointment?.id]);

  const setAns = (tplId, qid, val) => {
    setAnswers(a => ({ ...a, [`${tplId}::${qid}`]: val }));
    setErr("");
  };

  // For multi-choice we store an array of selected options. Toggle helper.
  const toggleMulti = (tplId, qid, option) => {
    setAnswers(a => {
      const key = `${tplId}::${qid}`;
      const current = Array.isArray(a[key]) ? a[key] : [];
      const next = current.includes(option) ? current.filter(o => o !== option) : [...current, option];
      return { ...a, [key]: next };
    });
    setErr("");
  };

  const isAnswered = (q, val) => {
    if (q.required === false) return true;
    if (q.type === "multi") return Array.isArray(val) && val.length > 0;
    if (q.type === "text")  return typeof val === "string" && val.trim().length > 0;
    // yesno / single / undefined-type → any truthy value
    return val !== undefined && val !== null && val !== "";
  };

  const submit = () => {
    // Validate required across every type
    for (const t of templates) {
      for (const q of (t.questions || [])) {
        const key = `${t.id}::${q.id}`;
        if (!isAnswered(q, answers[key])) {
          setErr(`Please answer: "${q.text}"`);
          return;
        }
        // If yes-no with follow-up enabled and answer is "yes", require detail text
        if ((q.type || "yesno") === "yesno" && q.follow_up_on_yes && answers[key] === "yes" && q.required !== false) {
          const detail = (answers[`${t.id}::${q.id}::detail`] || "").trim();
          if (!detail) {
            setErr(`Please fill in: "${q.follow_up_label || "details for " + q.text}"`);
            return;
          }
        }
      }
    }
    const payload = templates.map(t => ({
      template_id: t.id,
      template_name: t.name,
      answers: (t.questions || []).map(q => {
        const raw = answers[`${t.id}::${q.id}`];
        // Normalise array to comma-joined string for stored answers
        let answer = Array.isArray(raw) ? raw.join(", ") : (raw ?? null);
        // Append follow-up detail when present (e.g. "yes — Lisinopril, metformin")
        const detail = (answers[`${t.id}::${q.id}::detail`] || "").trim();
        if (answer === "yes" && detail) {
          answer = `Yes — ${detail}`;
        } else if (answer === "yes") {
          answer = "Yes";
        } else if (answer === "no") {
          answer = "No";
        }
        return { question_id: q.id, question: q.text, type: q.type || "yesno", answer };
      }),
    }));
    onComplete(payload);
  };

  if (loading) return <div style={{padding:30,textAlign:"center",color:C.muted}}>Loading questionnaire…</div>;
  if (templates.length === 0) {
    // Nothing to ask — skip past
    onComplete([]);
    return null;
  }

  // Renders the answer widget for a single question, dispatched on q.type:
  //   "yesno"  (default) — Yes/No buttons
  //   "single" — radio-style buttons (one selection from options)
  //   "multi"  — checkbox-style buttons (many selections from options)
  //   "text"   — free-text input (single line) or textarea (if q.long)
  const renderAnswerWidget = (t, q) => {
    const key = `${t.id}::${q.id}`;
    const val = answers[key];
    const type = q.type || "yesno";

    if (type === "yesno") {
      const detailKey = `${t.id}::${q.id}::detail`;
      const detailVal = answers[detailKey] || "";
      return (
        <div style={{display:"flex",flexDirection:"column",gap:6,width:"100%"}}>
          <div style={{display:"flex",gap:6}}>
            <button onClick={()=>setAns(t.id, q.id, "yes")} style={{padding:"7px 18px",background:val==="yes"?"linear-gradient(135deg,"+C.green+",#1e6b3f)":"#fff",color:val==="yes"?"#fff":C.text,border:"1.5px solid "+(val==="yes"?C.green:C.border),borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,minWidth:64}}>Yes</button>
            <button onClick={()=>{setAns(t.id, q.id, "no"); setAnswers(a => ({...a, [detailKey]: ""}));}} style={{padding:"7px 18px",background:val==="no" ?"linear-gradient(135deg,"+C.red+",#8a2a2a)" :"#fff",color:val==="no" ?"#fff":C.text,border:"1.5px solid "+(val==="no" ?C.red  :C.border),borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,minWidth:64}}>No</button>
          </div>
          {q.follow_up_on_yes && val === "yes" && (
            <div style={{marginTop:6,padding:"8px 10px",background:"rgba(46,139,87,0.06)",border:"1px solid "+C.green+"40",borderRadius:8}}>
              <div style={{fontSize:11,color:C.green,fontWeight:700,marginBottom:5}}>
                {q.follow_up_label || "Please list:"}{q.required !== false && <span style={{color:C.red}}> *</span>}
              </div>
              <textarea
                value={detailVal}
                onChange={e=>setAnswers(a => ({...a, [detailKey]: e.target.value}))}
                rows={2}
                placeholder="Type details here…"
                style={{width:"100%",padding:"8px 10px",background:"#fff",border:"1px solid "+C.border,borderRadius:6,fontSize:13,color:C.text,fontFamily:F,outline:"none",resize:"vertical",boxSizing:"border-box"}}
              />
            </div>
          )}
        </div>
      );
    }

    if (type === "single") {
      return (
        <div style={{display:"flex",gap:6,flexWrap:"wrap",width:"100%",marginTop:6}}>
          {(q.options || []).map(opt => (
            <button key={opt} onClick={()=>setAns(t.id, q.id, opt)} style={{padding:"7px 14px",background:val===opt?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"#fff",color:val===opt?"#fff":C.text,border:"1.5px solid "+(val===opt?C.gold:C.border),borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}>{opt}</button>
          ))}
        </div>
      );
    }

    if (type === "multi") {
      const arr = Array.isArray(val) ? val : [];
      return (
        <div style={{display:"flex",flexDirection:"column",gap:6,width:"100%",marginTop:6}}>
          {(q.options || []).map(opt => {
            const checked = arr.includes(opt);
            return (
              <button key={opt} onClick={()=>toggleMulti(t.id, q.id, opt)} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:checked?C.goldBg:"#fff",border:"1.5px solid "+(checked?C.gold:C.border),borderRadius:8,fontSize:12,color:C.text,cursor:"pointer",fontFamily:F,textAlign:"left"}}>
                <span style={{width:16,height:16,borderRadius:4,background:checked?C.gold:"#fff",border:"1.5px solid "+(checked?C.gold:C.dim),display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11,fontWeight:800,flexShrink:0}}>{checked?"✓":""}</span>
                <span>{opt}</span>
              </button>
            );
          })}
        </div>
      );
    }

    // text
    return q.long ? (
      <textarea value={val || ""} onChange={e=>setAns(t.id, q.id, e.target.value)} rows={3} placeholder="Type your answer…" style={{width:"100%",padding:"8px 10px",background:"#fff",border:"1px solid "+C.border,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",marginTop:6,resize:"vertical",boxSizing:"border-box"}}/>
    ) : (
      <input value={val || ""} onChange={e=>setAns(t.id, q.id, e.target.value)} placeholder="Type your answer…" style={{width:"100%",padding:"9px 12px",background:"#fff",border:"1px solid "+C.border,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",marginTop:6,boxSizing:"border-box"}}/>
    );
  };

  return (
    <div>
      <div style={{textAlign:"center",marginBottom:18}}>
        <div style={{fontSize:13,color:C.muted}}>Intake</div>
        <div style={{fontSize:20,fontWeight:800,color:C.gold,fontFamily:S}}>❓ A few quick questions</div>
        <div style={{fontSize:12,color:C.muted,marginTop:4}}>Please answer before {appointment?.treat || "your treatment"}</div>
      </div>

      {templates.map(t => (
        <div key={t.id} style={{marginBottom:20,padding:16,background:C.card,border:"1px solid "+C.border,borderRadius:12}}>
          <div style={{fontSize:14,fontWeight:700,color:C.text,fontFamily:S,marginBottom:4}}>{t.name}</div>
          {t.description && <div style={{fontSize:11,color:C.muted,marginBottom:10}}>{t.description}</div>}
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {(t.questions || []).map((q, i) => {
              const isInline = !q.type || q.type === "yesno";
              return (
                <div key={q.id || i} style={{padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8,display:"flex",alignItems:isInline?"center":"flex-start",gap:10,flexWrap:"wrap",flexDirection:isInline?"row":"column"}}>
                  <div style={{flex:1,minWidth:160,fontSize:13,color:C.text,fontWeight:500}}>{i+1}. {q.text}{q.required !== false && <span style={{color:C.red,marginLeft:3}}>*</span>}</div>
                  {renderAnswerWidget(t, q)}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {err && <div style={{padding:10,background:C.redL,color:C.red,borderRadius:8,fontSize:12,fontWeight:600,marginBottom:10}}>{err}</div>}

      <div style={{display:"flex",gap:8,justifyContent:"flex-end",flexWrap:"wrap"}}>
        {onCancel && <button onClick={onCancel} style={{padding:"10px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>Back</button>}
        <button
          onClick={() => {
            if (!window.confirm("Skip the intake questions and continue check-in?\n\nThe client will move to consents without answering these.")) return;
            onComplete([]);
          }}
          style={{padding:"10px 18px",background:"#fff",color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}
          title="Skip the questionnaire and move to consents"
        >
          ⏭ Skip questions
        </button>
        <button onClick={submit} style={{padding:"10px 22px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>Continue →</button>
      </div>
    </div>
  );
}
